import type { IRemoteModuleManifest } from '../types';
import type RemoteRuntime from '../RemoteRuntime';
import { getBestPublicPath } from '../utils/get-best-public-path';

// 记录资源 url 是否被做过预加载
const preloadUrlCache = new Set<string>();

/**
 * 基于 mf-build-info.json ，拿到需要加载的文件
 */
export default class PrefetchModuleHandler {
  // entry 是否被下载过
  private entryFetched?: boolean;
  // chunk 是否被下载过
  private chunkFetchedMap = new Map<string, boolean>();
  // container 名
  private readonly containerName: string;
  // 全局 runtime 实例，需要在 new 的时候传进来
  private readonly runtime: RemoteRuntime;
  // container 的描述文件
  private manifest?: IRemoteModuleManifest;
  // mf-build-info.json 内容
  private buildInfo?: Record<string, any>;
  // 用于缓存 fetch mf-build-info.json 的 promise
  private fetchBuildInfoPromise?: Promise<Record<string, any>>;

  constructor(options: { container: string; runtime: RemoteRuntime }) {
    this.containerName = options.container;

    this.runtime = options.runtime;

    const manifest = this.runtime.registry.get(this.containerName);
    if (manifest) {
      this.manifest = manifest;
    }

    const { containers } = this.runtime;
    // 如果该模块已经在 runtime 中，说明它的 entry.js 已经被加载过了，无需 prefetch
    if (containers[this.containerName]) {
      this.entryFetched = true;
    }
  }

  // 对外的 prefetchChunk 接口
  public async prefetchChunk(chunk: string) {
    if (this.chunkFetchedMap.has(chunk) || !this.manifest || !this.manifest.remoteEntry) {
      return;
    }

    // 目前这个机制只适配独立打包的 mf 场景, 所以 manifest.remoteEntry 一定存在
    const { url } = this.manifest.remoteEntry;
    if (!url || preloadUrlCache.has(url)) {
      return;
    }

    // 先 prefetch entry.js
    await this.prefetchEntry();

    // 再 prefetch chunk
    return this.purePrefetchChunk(chunk);
  }

  // prefetch entry.js
  private async prefetchEntry() {
    if (this.entryFetched || !this.manifest || !this.manifest.remoteEntry) {
      return;
    }

    const { url } = this.manifest.remoteEntry;
    await this.purePrefetchScript(url);
    this.entryFetched = true;
    preloadUrlCache.add(url);
    return;
  }

  // prefetch chunk 下的所有文件
  private async purePrefetchChunk(chunk: string) {
    if (this.chunkFetchedMap.has(chunk) || !this.manifest || !this.manifest.remoteEntry) {
      return;
    }

    const { buildInfoHash, publicPath, remoteEntry } = this.manifest;
    if (!buildInfoHash || !publicPath || !remoteEntry) {
      return;
    }
    // 初始化 buildInfo， 从 mf-build-info.json 中读取
    if (!this.buildInfo) {
      try {
        const { url } = remoteEntry;
        const buildInfoPath = `/mf-build-info.${buildInfoHash}.json`;
        // 把 /remote-entry-* 替换成 /mf-build-info.${buildInfoHash}.json
        const buildInfoUrl = url.replace(/\/remote-entry-[^/]+$/, buildInfoPath);
        const { bestPublicPath, currentPublicPath } = getBestPublicPath(this.manifest);
        const newUrl = buildInfoUrl.replace(currentPublicPath, bestPublicPath);
        if (!this.fetchBuildInfoPromise) {
          this.fetchBuildInfoPromise = fetch(newUrl).then(data => data.json());
        }
        const json = await this.fetchBuildInfoPromise;
        this.buildInfo = json.chunks || {};
        // eslint-disable-next-line local-rules/no-empty-catch
      } catch (e) {
        console.error(
          `[PrefetchModuleHandler] prefetch buildInfo failed, chunk: ${chunk}, error: ${e}`,
        );
      }
    }

    // 从 buildInfo 中读取 chunk 下的所有文件, 并 prefetch
    const chunkInfoList = this.buildInfo?.[chunk];
    if (chunkInfoList) {
      try {
        const urls = chunkInfoList
          .filter((item: { t: string; k: string }) => item.t === 'url')
          .map((item: { t: string; k: string }) => `${publicPath}${item.k}`);

        const fetchList = urls.map(async (file: string) => this.purePrefetchScript(file));
        await Promise.all(fetchList);
        this.chunkFetchedMap.set(chunk, true);
        // eslint-disable-next-line local-rules/no-empty-catch
      } catch (e) {
        console.error(
          `[PrefetchModuleHandler] prefetch chunk failed, chunk: ${chunk}, error: ${e}`,
        );
      }
    }
    return;
  }

  // 底层的 prefetch 方法实现
  private async purePrefetchScript(url: string) {
    const { bestPublicPath, currentPublicPath } = getBestPublicPath(this.manifest);
    const newUrl = url.replace(currentPublicPath, bestPublicPath);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = newUrl;
    link.as = 'script';
    document.head.appendChild(link);
    return new Promise((resolve, reject) => {
      link.onload = resolve;
      link.onerror = reject;
    });
  }
}
